import React, { useCallback, useContext, useEffect } from 'react';

import { Switch, Route } from 'react-router-dom';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useHotkeys } from 'react-hotkeys-hook';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { Flex, Input, Layout } from 'antd';

import { Stakes } from './Stakes/Stakes';
import Loader from 'components/Loading/Loader';
import Header from 'layout/header/Header';
import ProjectModal from 'components/Modal/ProjectModal';
import ProjectNavigation from 'components/navigation/ProjectNavigation';
import SituationAnalysisModal from 'components/Modal/SituationAnalysisModal';

import SubHeaderPortal from 'layout/header/SubHeaderPortal';
import {
	OnboardingProvider,
	OnboardingReviewBanner,
	useOnboardingActive,
	useOnboardingBodyClass,
	useOnboardingDerivedStep,
} from 'components/Onboarding/OnboardingReviewBanner';

import {
	useCreateRiskCategoryMutation,
	useCreateIndicatorCategoryMutation,
	useCreateTagMutation,
	useDeleteRiskCategoryMutation,
	useDeleteIndicatorCategoryMutation,
	useDeleteTagMutation,
	useUpdateProjectMutation,
	useUpdateRiskCategoryMutation,
	useUpdateIndicatorCategoryMutation,
	useUpdateTagMutation
} from 'graph/dist/generated';

import NavigatorContainer from './Navigator/NavigatorContainer';
import { Indicators } from './Indicators/Indicators';
import { Scenarios } from './Scenarios/Scenarios';
import { Fishbone } from './Fishbone/Fishbone';
import { Risks } from './Risks/Risks';

import './Project.sass';
import { isFunction, pickBy, sortBy } from 'lodash';
import { subscribeToMessages, useGetProjectQuery } from 'graph/queries';
import { useSubscription } from '@apollo/client';
import UIContext from 'components/Utils/UIContext';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';
import { useStakeholderController } from 'hooks/useStakeholderController';
import { Watch } from './Watch/Watch';
import { getItemAlteredByScenario } from 'utils/rules';

let lastData: any = null;

const limitFeatures = (props, projectData: any) => {

	// Un lecteur (projet partagé) doit voir les écrans que le TYPE du projet
	// supporte, indépendamment de SON abonnement (c'est le propriétaire qui a
	// payé les features). On bypasse donc le gating d'abonnement pour un reader ;
	// seul le filtrage par type de projet subsiste.
	const isReader = projectData?.getProject?.myRole === 'reader';
	const sub = (flag: boolean) => isReader ? true : flag;
	const type = projectData?.getProject?.type;

	return {
		...props,
		select: (a) => {
			props.select(a)
		},
		features: {
			...props.features,
			stakes: sub(props.features.stakes) && (type === 'pro') || type === 'risks' || type === 'stakeholders',
			indicators: sub(props.features.indicators) && (type === 'pro'),
			fishbone: sub(props.features.fishbone) && (type === 'pro'),
			watch: sub(props.features.indicators) && (type === 'pro'),
			risks: sub(props.features.risks) && (type === 'pro' || type === 'risks'),
			stakeholders: sub(props.features.stakeholders) && (type === 'pro' || type === 'stakeholders'),
			// Séparateur + scénarios avancés visibles pour un lecteur (sinon le
			// bloc scénario s'affiche sans son séparateur -> rupture visuelle).
			displayAdvancedScenarios: sub(props.features.displayAdvancedScenarios) && (type === 'pro' || type === 'risks'),
		}
	}
}

// When onboarding is active AND the current path matches a review step
// (stakes / risks / opportunities / stakeholders), the SubHeader shows the
// OnboardingReviewBanner. On any other path inside the project (e.g. /summary
// visu tab, /scenarios, etc.) the onboarding session stays alive in
// sessionStorage but we fall back to the normal navigation — otherwise the
// SubHeader is empty (banner returns null, menu is hidden), which left the
// user without any way to navigate back.
const OnboardingHeaderSwitch: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const active = useOnboardingActive();
	const step = useOnboardingDerivedStep();
	const showBanner = active && step !== null;
	// Body class follows banner visibility, NOT just active state. On paths
	// outside the review flow (visu, scenarios…) we render the normal nav and
	// must NOT inherit the header padding-reset / height override.
	useOnboardingBodyClass(showBanner);
	return showBanner ? <OnboardingReviewBanner /> : <>{children}</>;
};

export function Project(props: any) {

	let history = useHistory();
	const { t } = useTranslation();
	const params: any = useParams();
	const [editedItem, setEditedItem] = React.useState<any | null>(null);
	const [searchTerm, setSearchTerm] = React.useState<any | null>('');
	const [currentScenarioIndex, setCurrentScenarioIndex] = React.useState<
		number | null
	>(null);
	const [pendingScenarioId, setPendingScenarioId] = React.useState<string | null>(null);
	const [showSituationAnalysis, setShowSituationAnalysis] = React.useState(false);
	const uiContext = useContext(UIContext);


	useSubscription(
		subscribeToMessages,
		{
			variables: { projectId: params.id },
			onData: async (message) => await props.onReceiveProjectMessage(message?.data?.data?.onProjectMessage, data?.getProject)
		}
	);

	useEffect(() => {
		if (props.setExpanded) props.setExpanded(true);
		props.subscribeToProject(params.id);

		// Vérifier si on doit ouvrir l'analyse de situation
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get('openAnalysis') === 'true') {
			setShowSituationAnalysis(true);
			// Nettoyer l'URL
			history.replace(`/projects/${params.id}`);
		}

		return () => { };
	}, []);

	const [updateData] = useUpdateProjectMutation();
	const [createTag] = useCreateTagMutation();
	const [updateTag] = useUpdateTagMutation();
	const [deleteTag] = useDeleteTagMutation();

	const [createRiskCategory] = useCreateRiskCategoryMutation();
	const [updateRiskCategory] = useUpdateRiskCategoryMutation();
	const [deleteRiskCategory] = useDeleteRiskCategoryMutation();
	const [createIndicatorCategory] = useCreateIndicatorCategoryMutation();
	const [updateIndicatorCategory] = useUpdateIndicatorCategoryMutation();
	const [deleteIndicatorCategory] = useDeleteIndicatorCategoryMutation();

	const { data, refetch, loading } = useGetProjectQuery(params?.id);

	// Mode lecture seule : injecte le rôle du caller sur ce projet dans le
	// UIContext (canEdit/isReadOnly), pour neutraliser les affordances d'édition.
	// Reset à la sortie du projet pour ne pas verrouiller le portfolio.
	useEffect(() => {
		if (data?.getProject) {
			uiContext.setProjectRole?.(data.getProject.myRole);
		}
		return () => {
			uiContext.setProjectRole?.(null);
		};
	}, [data?.getProject?.id, data?.getProject?.myRole]);

	// Expose current project to window for debugging/usage
	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.project = data?.getProject ?? null;
		}
	}, [data?.getProject]);

	// Expose helper to create a stakeholder for current project
	const stakeholderController = useStakeholderController();
	useEffect(() => {
		if (typeof window === 'undefined') return;
		window.addStakeholder = async (values: any = {}, defaults: any = {}) => {
			console.log('projectId', params?.id);
			const projectId = params?.id;
			const stakeholders = data?.getProject?.stakeholders || [];
			return stakeholderController.createStakeholder(
				projectId,
				stakeholders as any,
				values,
				defaults
			);
		};
		return () => { /* keep global for session */ };
	}, [params?.id, data?.getProject?.stakeholders, stakeholderController]);

	let limitedProps = limitFeatures(props, data);

	const _refetch = useCallback(() => {
		setTimeout(() => refetch(), 0);
	}, [refetch]);

	const handleCreateTag = async (name: string, callback: Function) => {
		if (!name) return;

		const result = await createTag({
			variables: {
				name: name,
				projectId: params.id
			}
		});
		await _refetch();
		if (callback) await callback(result.data?.createTag?.id);
		await _refetch();
	};

	// Update project
	const handleUpdateProject = async (id: any, values: any) => {
		const tags = values.tags;
		delete values.tags;

		const riskCategories = Array.isArray(values.riskCategories) ? [...values.riskCategories] : [];
		delete values.riskCategories;

		const indicatorCategories = Array.isArray(values.indicatorCategories) ? [...values.indicatorCategories] : [];
		delete values.indicatorCategories;

		if (!values.likelihoods || values.likelihoods === 'null') {
			delete values.likelihoods;
		}

		if (!values.impacts || values.impacts === 'null') {
			delete values.impacts;
		}

		// Old tags that disappeared
		const oldTags = projectData?.getProject?.tags.filter(
			(t: any) => !tags?.find((tag: any) => tag.id === t.id)
		);

		// Create or update each tag if needed
		if (tags) {
			await Promise.all([
				...tags.map(async (tag: any) => {
					if (tag.id === undefined) {
						return createTag({
							variables: {
								name: tag.name,
								projectId: id
							}
						});
					} else {
						// Update if name changed
						const originalTag = projectData?.getProject?.tags.find(
							(t: any) => t.id === tag.id
						);
						if (originalTag?.name !== tag.name) {
							return updateTag({
								variables: {
									id: tag.id,
									name: tag.name
								}
							});
						}
					}
				}),
				...oldTags.map(async (tag: any) => {
					return deleteTag({
						variables: {
							id: tag.id
						}
					});
				})
			]);
		}

		// Old categories that disappeared
		const oldCategories = projectData?.getProject?.riskCategories.filter(
			(t: any) => !riskCategories?.find((tag: any) => tag.id === t.id)
		);

		// Create or update each tag if needed
		console.log('riskCategories', riskCategories);
		if (riskCategories && riskCategories.length > 0) {
			await Promise.all([
				...riskCategories.map(async (riskCategory: any) => {

					console.log('riskCategory', riskCategory);
					if (riskCategory.id === undefined || riskCategory.id.length < 36) {
						return createRiskCategory({
							variables: {
								name: riskCategory.name,
								projectId: id,
								weight: riskCategory.weight
							}
						});
					} else {
						// Update if name changed
						const originalRiskCategory =
							projectData?.getProject?.riskCategories.find(
								(t: any) => t.id === riskCategory.id
							);
						if (originalRiskCategory?.name !== riskCategory.name || originalRiskCategory?.weight !== riskCategory.weight) {
							return updateRiskCategory({
								variables: {
									id: riskCategory.id,
									name: riskCategory.name,
									weight: riskCategory.weight
								}
							});
						}
					}
				}),
				...oldCategories.map(async (riskCategory: any) => {
					return deleteRiskCategory({
						variables: {
							id: riskCategory.id
						}
					});
				})
			]);
		}

		// Catégories d'indicateurs : même diff create/update/delete que les
		// catégories de risques.
		const oldIndicatorCategories = (projectData?.getProject?.indicatorCategories || []).filter(
			(c: any) => !indicatorCategories?.find((ic: any) => ic.id === c.id)
		);
		if (indicatorCategories && indicatorCategories.length > 0) {
			// Créations SÉQUENTIELLES : l'ordre de création (createdAt) porte
			// l'ordre des quadrants BSC dans la viz.
			for (const indicatorCategory of indicatorCategories) {
				if (indicatorCategory.id === undefined || indicatorCategory.id.length < 36) {
					await createIndicatorCategory({
						variables: {
							name: indicatorCategory.name,
							projectId: id,
							weight: indicatorCategory.weight
						}
					});
				}
			}
			await Promise.all([
				...indicatorCategories.map(async (indicatorCategory: any) => {
					if (indicatorCategory.id === undefined || indicatorCategory.id.length < 36) {
						return null; // déjà créé séquentiellement ci-dessus
					} else {
						// Update if name/weight changed
						const original =
							projectData?.getProject?.indicatorCategories?.find(
								(c: any) => c.id === indicatorCategory.id
							);
						if (original?.name !== indicatorCategory.name || original?.weight !== indicatorCategory.weight) {
							return updateIndicatorCategory({
								variables: {
									id: indicatorCategory.id,
									name: indicatorCategory.name,
									weight: indicatorCategory.weight
								}
							});
						}
					}
				}),
				...oldIndicatorCategories.map(async (indicatorCategory: any) => {
					return deleteIndicatorCategory({
						variables: {
							id: indicatorCategory.id
						}
					});
				})
			]);
		}

		if (values.likelihoods) {
			values.likelihoods = values.likelihoods.map((l, index) => ({ id: index + 1, name: l.name }))
		} else {
			delete values.likelihoods
		}

		if (values.impacts) {
			values.impacts = values.impacts.map((l, index) => ({ id: index + 1, name: l.name }))
		} else {
			delete values.impacts
		}

		if (values.fishboneEndOn) values.fishboneEndOn = values.fishboneEndOn.format("YYYY-MM-DD")
		if (values.fishboneStartOn) values.fishboneStartOn = values.fishboneStartOn.format("YYYY-MM-DD")

		values = pickBy(values, (v) => !isFunction(v) && !v?.props);

		const result = await updateData({
			variables: {
				id: id,
				...values
			}
		});

		await _refetch();
	};

	const onGoNext = (url: string) => {
		history.push(url);
	};

	if (data) {
		lastData = data;
	}

	const projectData = data || lastData;
	// Le fetch getProject omet certaines sections selon le type de projet
	// (risks/stakeholders -> pas de fishbone/indicateurs ; stakeholders -> pas
	// de risques). On normalise a [] pour que les enfants (Navigator toujours
	// monte, pages routees) puissent iterer sans garde.
	const projectForChildren = projectData?.getProject
		? {
				...projectData.getProject,
				risks: projectData.getProject.risks || [],
				stakeholders: projectData.getProject.stakeholders || [],
				indicators: projectData.getProject.indicators || [],
				fishboneBranchs: projectData.getProject.fishboneBranchs || []
		  }
		: projectData?.getProject;
	const sortedScenarios = sortBy(projectData?.getProject?.scenarios || [], "name");
	const portalContent = (
		<>
			<div>
				<div className="Title">
					{projectData?.getProject?.name
						? projectData?.getProject?.name
						: projectData?.getProject
							? 'No name'
							: ''}
				</div>
				<div className="SubTitle">
					<small>
						{projectData?.getProject?.description
							? projectData?.getProject?.description
							: projectData?.getProject
								? null
								: null}
					</small>
				</div>
			</div>

			<FontAwesomeIcon
				style={{
					marginLeft: '0.5rem',
					fontSize: '1.2rem',
					cursor: 'pointer'
				}}
				icon={faEdit}
				onClick={() => setEditedItem(projectData?.getProject)}
			/>
		</>
	);

	const currentScenario =
		currentScenarioIndex !== null
			? sortedScenarios[currentScenarioIndex]
			: null;

	// Désélectionner l'élément s'il est disabled dans le nouveau scénario
	useEffect(() => {
		if (!uiContext.selection) return;

		// Vérifier si l'élément est enabled dans le scénario actuel
		const alteredItem = getItemAlteredByScenario(uiContext.selection, currentScenario);
		const isEnabled = alteredItem?.enabled !== false;

		if (!isEnabled) {
			uiContext.deselect(null);
		}
	}, [currentScenarioIndex]);

	const handleGoToPreviousScenario = () => {
		if (
			currentScenarioIndex === 0 &&
			sortedScenarios?.length > 0
		) {
			setCurrentScenarioIndex(null);
		} else if (currentScenarioIndex > 0) {
			setCurrentScenarioIndex(currentScenarioIndex - 1);
		}
	};

	const handleGoToNextScenario = () => {
		if (
			currentScenarioIndex === null &&
			sortedScenarios?.length > 0
		) {
			setCurrentScenarioIndex(0);
		} else if (currentScenarioIndex === null) {
		} else if (
			currentScenarioIndex <
			sortedScenarios?.length - 1
		) {
			setCurrentScenarioIndex(currentScenarioIndex + 1);
		}
	};

	// Change scenario with arrow keys
	useHotkeys('ArrowRight', handleGoToNextScenario);
	useHotkeys('ArrowLeft', handleGoToPreviousScenario);

	const handleGoToScenario = (indexOrId: number | string | null) => {
		// Si c'est un ID de scénario (string), trouver son index dans la liste triée
		if (typeof indexOrId === 'string') {
			const index = sortedScenarios.findIndex((s: any) => s.id === indexOrId);
			if (index !== -1) {
				setCurrentScenarioIndex(index);
				setPendingScenarioId(null);
			} else {
				// Scénario pas encore dans la liste, le stocker pour navigation différée
				console.log('🔵 Scenario not yet in list, storing for deferred navigation:', indexOrId);
				setPendingScenarioId(indexOrId);
			}
		} else {
			// Si c'est un index (number) ou null (baseline)
			setCurrentScenarioIndex(indexOrId);
			setPendingScenarioId(null);
		}
	};

	// Effect pour naviguer vers un scénario en attente quand il apparaît dans la liste
	useEffect(() => {
		if (pendingScenarioId && sortedScenarios.length > 0) {
			const index = sortedScenarios.findIndex((s: any) => s.id === pendingScenarioId);
			if (index !== -1) {
				console.log('🟢 Pending scenario found in list, navigating to index:', index);
				setCurrentScenarioIndex(index);
				setPendingScenarioId(null);
			}
		}
	}, [sortedScenarios, pendingScenarioId]);

	const searchBar = (
		<Input
			onChange={(e) => {
				setSearchTerm(e.target.value);
			}}
			value={searchTerm}
			style={{ marginLeft: 'auto', marginRight: '1em' }}
			placeholder="Search"
			className="SearchInput"
		></Input>
	);


	return (
		<OnboardingProvider>
		<Layout
			className={['Project', !limitedProps.expanded ? 'visible' : ''].join(' ')}
		>
			<Header></Header>

			<SubHeaderPortal>
				<OnboardingHeaderSwitch>
				<MaskableComponent masked={uiContext.maskedZones?.header}>
					<ProjectNavigation
						loading={loading && !projectData?.getProject?.name}
						{...limitedProps}
						onExpand={() => {
							limitedProps.setExpanded(true);
						}}
						onShrink={() => {
							limitedProps.setExpanded(false);
						}}
						project={projectForChildren}
						scenario={currentScenario}
						scenarioIndex={currentScenarioIndex}
						scenarioCount={sortedScenarios?.length}
						onGoToPreviousScenario={handleGoToPreviousScenario}
						onGoToNextScenario={handleGoToNextScenario}
						onGoToScenario={handleGoToScenario}

						onUpdate={handleUpdateProject}
						{...projectForChildren}

						displayIndicators={limitedProps.features?.indicators}
						displayStakes={limitedProps.features?.stakes}
						displayFishbone={limitedProps.features?.fishbone}
						displayWatch={limitedProps.features?.watch}
						displayRisks={limitedProps.features?.risks}
						displayStakeholders={limitedProps.features?.stakeholders}
						displayAdvancedScenarios={limitedProps.features?.displayAdvancedScenarios}
						pushModal={limitedProps.pushModal}
						popModal={limitedProps.popModal}

						refetch={_refetch}
						onOpenSituationAnalysis={() => setShowSituationAnalysis(true)}
					></ProjectNavigation>

				</MaskableComponent>
				</OnboardingHeaderSwitch>
			</SubHeaderPortal>

			<Layout.Content>

				{loading ? (
					<Flex vertical align="center" justify="center" className='mt-4'>
						<Loader></Loader>
					</Flex>

				) : (
					<>
						<Switch>
							<Route key="navigator" exact path="/projects/:id">
								<NavigatorContainer
									{...projectForChildren}
									refetch={_refetch}
									{...limitedProps}
									onCreateTag={handleCreateTag}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									scenario={currentScenario}
									scenarios={sortedScenarios}
									displayIndicators={limitedProps.features?.indicators}
									displayStakes={limitedProps.features?.stakes}
									displayRisks={limitedProps.features?.risks}
									portalContent={
										<>
											{portalContent}
											{searchBar}
										</>
									}
								/>
							</Route>

							<Route
								key="project"
								path={`/projects/:id/${t('pages.stakes.url')}`}
								render={() => (
									<Stakes
										onGoNext={onGoNext}
										refetch={_refetch}
										{...limitedProps}
										onEditProject={() =>
											setEditedItem(projectData?.getProject)
										}
										onCreateTag={handleCreateTag}
										scenario={currentScenario}
										scenarioIndex={currentScenarioIndex}
										scenarioCount={sortedScenarios?.length}
										onGoToPreviousScenario={handleGoToPreviousScenario}
										onGoToNextScenario={handleGoToNextScenario}
										onGoToScenario={handleGoToScenario}
										{...projectForChildren}
										searchTerm={searchTerm}
										setSearchTerm={setSearchTerm}
										scenarios={sortedScenarios}
										portalContent={portalContent}
										displayRisks={limitedProps.features?.risks}
									></Stakes>
								)}
							></Route>

							<Route
								key="project"
								path={`/projects/:id/${t('pages.indicators.url')}`}
								render={() => (
									<Indicators
										onGoNext={onGoNext}
										refetch={_refetch}
										{...limitedProps}
										onEditProject={() =>
											setEditedItem(projectData?.getProject)
										}
										onCreateTag={handleCreateTag}
										scenario={currentScenario}
										{...projectForChildren}
										searchTerm={searchTerm}
										setSearchTerm={setSearchTerm}
										scenarios={sortedScenarios}
										portalContent={portalContent}
										onGoToPreviousScenario={handleGoToPreviousScenario}
										onGoToNextScenario={handleGoToNextScenario}
									></Indicators>
								)}
							></Route>

							<Route
								key="project"
								path={`/projects/:id/${t('pages.watch.url')}`}
								render={() => (
									<Watch
										{...limitedProps}
										{...projectForChildren}
									></Watch>
								)}
							></Route>

							<Route
								key="project"
								path="/projects/:id/scenarios"
								render={() => (
									<Scenarios
										onGoNext={onGoNext}
										refetch={_refetch}
										onGoToScenario={handleGoToScenario}
										{...limitedProps}
										scenario={currentScenario}
										scenarioIndex={currentScenarioIndex}
										onGoToPreviousScenario={
											handleGoToPreviousScenario
										}
										onGoToNextScenario={handleGoToNextScenario}
										onEditProject={() =>
											setEditedItem(projectData?.getProject)
										}
										{...projectForChildren}
										scenarios={sortedScenarios}
										portalContent={portalContent}
									></Scenarios>
								)}
							></Route>

							<Route
								key="project"
								path="/projects/:id/fishbone"
								render={() => (
									<Fishbone
										onGoNext={onGoNext}
										refetch={_refetch}
										{...limitedProps}
										onUpdateProject={handleUpdateProject}
										onCreateTag={handleCreateTag}
										scenario={currentScenario}
										scenarioIndex={currentScenarioIndex}
										onGoToPreviousScenario={
											handleGoToPreviousScenario
										}
										onGoToNextScenario={handleGoToNextScenario}
										onEditProject={() =>
											setEditedItem(projectData?.getProject)
										}
										searchTerm={searchTerm}
										setSearchTerm={setSearchTerm}
										{...projectForChildren}
										scenarios={sortedScenarios}
										portalContent={portalContent}
									></Fishbone>
								)}
							></Route>

							<Route
								key="project"
								path="/projects/:id/:view?"
								render={() => (
									<Risks
										onGoNext={onGoNext}
										refetch={_refetch}
										{...limitedProps}
										onUpdateProject={handleUpdateProject}
										onCreateTag={handleCreateTag}
										scenario={currentScenario}
										scenarioIndex={currentScenarioIndex}
										setSearchTerm={setSearchTerm}
										searchTerm={searchTerm}
										onGoToPreviousScenario={
											handleGoToPreviousScenario
										}
										onGoToNextScenario={handleGoToNextScenario}
										onEditProject={() =>
											setEditedItem(projectData?.getProject)
										}
										{...projectForChildren}
										scenarios={sortedScenarios}
										portalContent={portalContent}
									></Risks>
								)}
							></Route>


						</Switch>
						{limitedProps.children}
					</>
				)}
				{editedItem && (
					<ProjectModal
						onHide={() => {
							setEditedItem(null);
						}}
						onSave={async (values: Object) => {
							await handleUpdateProject(editedItem.id, values);
							setEditedItem(null);
						}}
						id={editedItem?.id}
						key={editedItem?.id}
						values={{
							name: editedItem?.name,
							reference: editedItem?.reference,
							description: editedItem?.description,
							descriptionLong: editedItem?.descriptionLong,
							vision: editedItem?.vision,
							likelihoods: editedItem?.likelihoods,
							impacts: editedItem?.impacts,
							tags: editedItem?.tags,
							riskCategories: editedItem?.riskCategories,
							indicatorCategories: editedItem?.indicatorCategories,
							type: editedItem?.type,
						}}
						authorizedTypes={props.authorizedProjectTypes}
					></ProjectModal>
				)}

				{showSituationAnalysis && (
					<SituationAnalysisModal
						visible={showSituationAnalysis}
						onClose={() => setShowSituationAnalysis(false)}
						onComplete={() => setShowSituationAnalysis(false)}
						projectId={params.id}
						projectName={projectData?.getProject?.name}
					/>
				)}

			</Layout.Content>
		</Layout>
		</OnboardingProvider>
	);
}